
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserAccountNav } from '../ui/user-account-nav';
import { ThemeToggle } from '../ui/theme-toggle';
import { Logo } from '../ui/logo';
import { Button } from '../ui/button';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export function PublicNavbar() {
  const {
    isAuthenticated,
    user
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  // Close menu when changing from mobile to desktop
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Handle route changes on mobile
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname]);
  
  return <nav className="fixed top-0 left-0 right-0 bg-background z-50 border-b">
      <div className="container flex h-16 items-center justify-between px-4">
        <div className="flex items-center">
          <Logo />
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 ml-8">
            <NavLink to="/destinations" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Destinations
            </NavLink>
            <NavLink to="/itineraries" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Itineraries
            </NavLink>
            <NavLink to="/premium-itineraries" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>Premium Itineraries</NavLink>
            <NavLink to="/flights" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Flights
            </NavLink>
            <NavLink to="/blog" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Blog
            </NavLink>
            <NavLink to="/travel-bytes" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Travel Bytes
            </NavLink>
            <NavLink to="/travel-resources" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Travel Resources
            </NavLink>
            <NavLink to="/deals" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Deals
            </NavLink>
            <NavLink to="/travel-quiz" className={({
            isActive
          }) => cn("px-3 py-2 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")}>
              Quiz
            </NavLink>
          </div>
        </div>

        <div className="flex items-center">
          <div className="hidden md:flex items-center space-x-2">
            <ThemeToggle />
            
            {!isAuthenticated && <NavLink to="/login">
                <Button variant="default">Log In</Button> 
              </NavLink>}
            
            {isAuthenticated && user && <div className="flex items-center ml-4">
                <UserAccountNav user={user} />
              </div>}
          </div>
          
          {/* Mobile menu button */}
          <div className="flex md:hidden items-center space-x-2">
            <ThemeToggle />
            <Button variant="outline" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-expanded={mobileMenuOpen}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={cn("fixed inset-x-0 top-16 bottom-0 bg-background border-t z-50 transform transition-transform md:hidden overflow-y-auto", mobileMenuOpen ? "translate-y-0" : "translate-y-full")}>
        <div className="container flex flex-col pt-4 pb-safe-area-bottom space-y-1">
          <NavLink to="/destinations" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Destinations
          </NavLink>
          <NavLink to="/itineraries" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Itineraries
          </NavLink>
          <NavLink to="/premium-itineraries" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Premium Plans
          </NavLink>
          <NavLink to="/flights" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Flights
          </NavLink>
          <NavLink to="/blog" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Blog
          </NavLink>
          <NavLink to="/travel-bytes" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Travel Bytes
          </NavLink>
          <NavLink to="/travel-resources" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            Travel Resources
          </NavLink>
          <NavLink to="/travel-news" className={({
          isActive
        }) => cn("px-3 py-4 rounded-md text-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors", isActive ? "bg-accent text-accent-foreground" : "text-foreground/80")} onClick={() => setMobileMenuOpen(false)}>
            News
          </NavLink>
          
          <div className="mt-4 border-t pt-4">
            {!isAuthenticated ? <NavLink to="/login" className="w-full block" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full" variant="default">Log In</Button>
              </NavLink> : user ? <div className="flex items-center">
                <UserAccountNav user={user} />
                <NavLink to="/dashboard" className="ml-2 w-full block" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline">Dashboard</Button>
                </NavLink>
              </div> : null}
          </div>
        </div>
      </div>
    </nav>;
}
